import { DrawingType } from '../types/project'
import { useDrawingStore } from '../stores/drawingStore'
import { 
  Zap, 
  Box, 
  Circle, 
  ToggleLeft, 
  Signpost, 
  Cable,
  CircuitBoard,
  BarChart3
} from 'lucide-react'

interface SidePanelProps {
  currentTab: DrawingType
  onAddComponent: (type: string) => void
  onShowEstimate: () => void
}

const SidePanel = ({ onAddComponent, onShowEstimate }: SidePanelProps) => {
  const { mode, setMode, selectedWireType, setSelectedWireType } = useDrawingStore()

  const handleAddComponent = (type: string) => {
    onAddComponent(type)
  }

  const handleSetLineMode = (type: 'power' | 'control') => {
    setSelectedWireType(type)
    setMode('line')
  }

  return (
    <aside className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto">
      {/* 部品パレット */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Box size={16} />
          部品
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => handleAddComponent('EV充電器')}
            className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded text-sm text-blue-900 transition"
          >
            <Zap size={16} />
            EV充電器
          </button>
          <button
            onClick={() => handleAddComponent('分電盤')}
            className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 rounded text-sm text-orange-900 transition"
          >
            <CircuitBoard size={16} />
            分電盤
          </button>
          <button
            onClick={() => handleAddComponent('ブレーカー')}
            className="w-full flex items-center gap-2 px-3 py-2 bg-red-50 hover:bg-red-100 rounded text-sm text-red-900 transition"
          >
            <ToggleLeft size={16} />
            ブレーカー
          </button>
          <button
            onClick={() => handleAddComponent('プルボックス')}
            className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded text-sm text-gray-900 transition"
          >
            <Box size={16} />
            プルボックス
          </button>
          <button
            onClick={() => handleAddComponent('案内板')}
            className="w-full flex items-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 rounded text-sm text-green-900 transition"
          >
            <Signpost size={16} />
            案内板
          </button>
          <button
            onClick={() => handleAddComponent('電柱')}
            className="w-full flex items-center gap-2 px-3 py-2 bg-yellow-50 hover:bg-yellow-100 rounded text-sm text-yellow-900 transition"
          >
            <Cable size={16} />
            電柱
          </button>
        </div>
      </section>

      {/* 線種選択 */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Cable size={16} />
          配線
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => handleSetLineMode('power')}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition ${
              mode === 'line' && selectedWireType === 'power'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-50 hover:bg-blue-100 text-blue-900'
            }`}
          >
            電源線を引く
          </button>
          <button
            onClick={() => handleSetLineMode('control')}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition ${
              mode === 'line' && selectedWireType === 'control'
                ? 'bg-green-600 text-white'
                : 'bg-green-50 hover:bg-green-100 text-green-900'
            }`}
          >
            制御線を引く
          </button>
        </div>
      </section>

      {/* 図形ツール */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Circle size={16} />
          図形
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => setMode('rect')}
            className="w-full px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded text-sm text-purple-900 transition"
          >
            矩形を描く
          </button>
          <button
            onClick={() => setMode('circle')}
            className="w-full px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded text-sm text-purple-900 transition"
          >
            円を描く
          </button>
          <button
            onClick={() => setMode('text')}
            className="w-full px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded text-sm text-purple-900 transition"
          >
            テキスト追加
          </button>
        </div>
      </section>

      {/* 見積 */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BarChart3 size={16} />
          見積
        </h3>
        <button 
          onClick={onShowEstimate}
          className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm transition"
        >
          見積を算出
        </button>
      </section>

      {/* 現在のモード表示 */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          現在のモード: <span className="font-semibold">{mode}</span>
        </p>
      </div>
    </aside>
  )
}

export default SidePanel
